import { getWallet } from '@services/wallet.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { WalletDeductedEvent, WalletRechargedEvent } from '@witzy/shared';
import { useCallback, useEffect, useState } from 'react';

import { useSocket } from './useSocket';

/**
 * Combines React Query (initial load) with Socket.io (real-time wallet
 * balance changes). Any wallet:deducted or wallet:recharged event
 * instantly updates the local balance without waiting for a refetch.
 */
export function useWalletBalance(): {
  balance: number;
  isLoading: boolean;
  isLow: boolean;
  isEmpty: boolean;
} {
  const qc = useQueryClient();
  const { on, off } = useSocket();

  const { data, isLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: getWallet,
    staleTime: 30_000,
  });

  const [realtimeBalance, setRealtimeBalance] = useState<number | null>(null);
  // Coerce at the boundary. `Wallet.balance` is typed as a number, but it is a
  // Postgres numeric column and arrives over the wire as a STRING ("2999.3800").
  // TypeScript can't catch the mismatch, and the bug is invisible in arithmetic
  // (JS coerces for `<`) — it only shows up in formatting, where
  // String.prototype.toLocaleString silently ignores the digit options and
  // renders the raw 4-decimal database value.
  const balance = Number(realtimeBalance ?? data?.balance ?? 0) || 0;

  const onDeducted = useCallback((raw: unknown) => {
    const event = raw as WalletDeductedEvent;
    setRealtimeBalance(event.new_balance);
    void qc.invalidateQueries({ queryKey: ['wallet'] });
  }, [qc]);

  const onRecharged = useCallback((raw: unknown) => {
    const event = raw as WalletRechargedEvent;
    setRealtimeBalance(event.new_balance);
    void qc.invalidateQueries({ queryKey: ['wallet'] });
    // B.7 — Reactivate-on-recharge story: a recharge can flip a
    // past_due / suspended subscription back to active server-side
    // (Batch C auto-reactivate), and at minimum it changes the deficit
    // shown on the paywall page. Force a re-fetch so the UI catches
    // up without the user having to navigate away.
    void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
    void qc.invalidateQueries({ queryKey: ['paywall-info'] });
  }, [qc]);

  useEffect(() => {
    on('wallet:deducted', onDeducted);
    on('wallet:recharged', onRecharged);
    return () => {
      off('wallet:deducted', onDeducted);
      off('wallet:recharged', onRecharged);
    };
  }, [on, off, onDeducted, onRecharged]);

  // Reset realtime override when React Query fetches fresh data.
  useEffect(() => {
    if (data) setRealtimeBalance(null);
  }, [data]);

  return {
    balance,
    isLoading,
    isLow: balance < 500 && balance >= 0.01,
    isEmpty: balance < 0.01,
  };
}
